import { Fragment } from "react";
import { Link } from "react-router-dom";
import { Info, AlertTriangle, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { openConsentPreferences } from "@/lib/consent";

const TOKEN = /(\[[^\]]+\]\([^)]+\)|À compléter|À définir|À confirmer|À finaliser avant activation commerciale)/g;

export const Rich = ({ text }) => (
  <>
    {String(text).split(TOKEN).map((part, i) => {
      const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (link) return <Link key={i} to={link[2]} className="font-medium text-amber-600 underline-offset-2 hover:underline dark:text-amber-400">{link[1]}</Link>;
      if (TOKEN.test(part)) { TOKEN.lastIndex = 0; return <span key={i} data-testid="legal-tbd" className="rounded bg-amber-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300">{part}</span>; }
      return <Fragment key={i}>{part}</Fragment>;
    })}
  </>
);

const DocTable = ({ head, rows }) => (
  <>
    <div className="hidden overflow-hidden rounded-lg border border-border md:block">
      <table className="w-full text-left text-sm">
        <thead className="bg-muted/60"><tr>{head.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-border align-top">{r.map((c, j) => <td key={j} className="px-3 py-2"><Rich text={c} /></td>)}</tr>)}</tbody>
      </table>
    </div>
    <div className="space-y-3 md:hidden">
      {rows.map((r, i) => (
        <dl key={i} className="rounded-lg border border-border p-3 text-sm">
          {r.map((c, j) => <div key={j} className="py-1"><dt className="text-xs font-semibold text-muted-foreground">{head[j]}</dt><dd><Rich text={c} /></dd></div>)}
        </dl>
      ))}
    </div>
  </>
);

const Callout = ({ text, tone }) => {
  const Icon = tone === "warn" ? AlertTriangle : Info;
  return (
    <div className={`flex gap-3 rounded-lg border p-4 text-sm ${tone === "warn" ? "border-amber-500/50 bg-amber-500/10" : "border-sky-500/40 bg-sky-500/10"}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" /><p><Rich text={text} /></p>
    </div>
  );
};

const Block = ({ b }) => {
  if (typeof b === "string") return <p className="leading-relaxed text-foreground/90"><Rich text={b} /></p>;
  if (b.list) return <ul className="list-disc space-y-1.5 pl-5 leading-relaxed">{b.list.map((t) => <li key={t}><Rich text={t} /></li>)}</ul>;
  if (b.table) return <DocTable {...b.table} />;
  if (b.callout) return <Callout text={b.callout} tone={b.tone} />;
  if (b.action === "consent-prefs") return (
    <Button variant="outline" onClick={openConsentPreferences} data-testid="manage-consent-button" className="gap-2">
      <SlidersHorizontal className="h-4 w-4" /> Gérer mes préférences
    </Button>
  );
  return null;
};

const Toc = ({ sections }) => (
  <nav aria-label="Sommaire" data-testid="info-toc" className="rounded-lg border border-border bg-card p-4">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sommaire</p>
    <ol className="grid gap-1 text-sm sm:grid-cols-2">
      {sections.map((s) => (
        <li key={s.id}>
          <a href={`#${s.id}`} data-testid={`toc-link-${s.id}`} className="text-muted-foreground hover:text-amber-500"
            onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" }); }}>
            {s.title}
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

export const DocRenderer = ({ doc }) => (
  <div className="space-y-8">
    {doc.intro && <p className="text-base leading-relaxed text-muted-foreground"><Rich text={doc.intro} /></p>}
    {doc.draft && <Callout tone="warn" text="Version de travail à valider juridiquement avant exploitation commerciale. Les éléments marqués « À compléter » ou « À définir » ne sont pas inventés." />}
    {doc.sections.length > 3 && <Toc sections={doc.sections} />}
    {doc.sections.map((s) => (
      <section key={s.id} id={s.id} data-testid={`info-section-${s.id}`} className="scroll-mt-6 space-y-3">
        <h2 className="font-heading text-lg font-semibold">{s.title}</h2>
        {s.blocks.map((b, i) => <Block key={i} b={b} />)}
      </section>
    ))}
  </div>
);
